/**
 * packages/inference-schema/src/credential-fields.ts
 *
 * WHICH credential inputs each auth method needs — the single source shared by the connect
 * form (rendering), the api service (validation), and the tenant vault (stored shape).
 *
 * Data-driven on purpose. The connect form used to hardcode inputs for `api-key` and `bearer`
 * only, while offering `oauth2` and `basic` in the same dropdown: choosing either rendered no
 * fields and submitted no credentials, and the connection then silently failed to
 * authenticate. Rendering from this table means a method cannot be listed without its inputs
 * existing, so that failure cannot recur by omission.
 *
 * Lives here, not in @inferrex/service-schema (where `ProviderAuthMethod` is declared), because
 * the browser bundle needs it and depends on this package. `AuthMethod` is generated from
 * inferrex-ai/canonical-model/manifest.ts and kept identical to `ProviderAuthMethod` by the
 * vocab-parity generator, so keying off it here is the same vocabulary either way.
 */

import type { AuthMethod } from './generated/model.generated.js';

/** One credential input, rendered by the connect form and validated on the server. */
export interface CredentialField {
  /** Field name as stored in the credential JSON — the contract between form, vault and sync. */
  readonly name: string;
  readonly label: string;
  /**
   * `secret` masks the input and is never echoed back; `text` is safe to redisplay; `list` is a
   * set of tokens entered one chip at a time and STORED space-delimited — the wire format an
   * OAuth `scope` parameter takes (RFC 6749 §3.3), so the vault, the token request and the
   * scopes view all read the same string. A `list` is safe to redisplay.
   */
  readonly kind: 'secret' | 'text' | 'list';
  readonly required: boolean;
  readonly placeholder?: string | undefined;
  readonly hint?: string | undefined;
}

export const CREDENTIAL_FIELD_SPEC: Readonly<Record<AuthMethod, readonly CredentialField[]>> = {
  'none': [],
  'api-key': [
    { name: 'apiKey', label: 'API key', kind: 'secret', required: true, hint: 'Sent as the value of the header below on every request. Stored encrypted; never shown again.' },
    { name: 'headerName', label: 'Header name', kind: 'text', required: false, placeholder: 'X-API-Key', hint: 'Defaults to X-API-Key if the system does not specify one.' },
  ],
  'bearer': [
    { name: 'token', label: 'Bearer token', kind: 'secret', required: true, hint: 'Sent as "Authorization: Bearer <token>" on every request. A Stripe secret or restricted key goes here.' },
  ],
  'basic': [
    { name: 'username', label: 'Username', kind: 'text', required: true, hint: 'Sent with the password as HTTP Basic authentication.' },
    { name: 'password', label: 'Password', kind: 'secret', required: true, hint: 'Stored encrypted; never shown again.' },
  ],
  'aws-sigv4': [
    { name: 'accessKeyId', label: 'Access key ID', kind: 'text', required: true, hint: 'Identifies the key pair. Each request is signed; the secret itself is never sent.' },
    { name: 'secretAccessKey', label: 'Secret access key', kind: 'secret', required: true, hint: 'Used only to sign requests. Stored encrypted; never shown again.' },
    { name: 'region', label: 'Region', kind: 'text', required: true, placeholder: 'eu-west-2', hint: 'Cloudflare R2 uses "auto".' },
    { name: 'service', label: 'Service', kind: 'text', required: true, placeholder: 's3', hint: 'S3-compatible storage (R2, MinIO, Wasabi, B2) is "s3".' },
    { name: 'sessionToken', label: 'Session token', kind: 'secret', required: false, hint: 'Only for temporary STS credentials.' },
  ],
  'oauth2': [
    { name: 'clientId', label: 'Client ID', kind: 'text', required: true, hint: 'From the app you registered with the provider (Salesforce: the External Client App\'s Consumer Key; Xero: the Custom Connection\'s client id).' },
    { name: 'clientSecret', label: 'Client secret', kind: 'secret', required: true, hint: 'Exchanged with the client id for a short-lived access token (OAuth client credentials). Inferrex renews the token before it expires.' },
    { name: 'tokenUrl', label: 'Token URL', kind: 'text', required: true, placeholder: 'https://example.com/oauth/token', hint: 'The provider\'s token endpoint, not its API. Salesforce: https://<your-domain>.my.salesforce.com/services/oauth2/token. Xero: https://identity.xero.com/connect/token.' },
    { name: 'scopes', label: 'Scopes', kind: 'list', required: false, placeholder: 'read:data', hint: 'One scope per entry (type it and press Enter), and only scopes the app has been granted. Salesforce: api. Leave empty if the provider fixes them on the app.' },
    { name: 'headerName', label: 'Token header', kind: 'text', required: false, placeholder: 'Authorization', hint: 'Leave blank to send "Authorization: Bearer <token>". Name a header only if the provider wants the token bare in its own header (Shopify: X-Shopify-Access-Token).' },
  ],
  'jwt': [
    { name: 'token', label: 'JWT', kind: 'secret', required: true, hint: 'A pre-issued token. For tokens Inferrex should mint, use OAuth 2.0.' },
  ],
  'mtls': [
    { name: 'clientCert', label: 'Client certificate (PEM)', kind: 'secret', required: true },
    { name: 'clientKey', label: 'Client private key (PEM)', kind: 'secret', required: true },
    { name: 'caCert', label: 'CA certificate (PEM)', kind: 'secret', required: false, hint: 'Only if the server uses a private CA.' },
  ],
  'custom': [
    { name: 'headerName', label: 'Header name', kind: 'text', required: true, placeholder: 'X-Auth-Token', hint: 'The exact header the provider\'s documentation names, e.g. X-Auth-Token.' },
    { name: 'headerValue', label: 'Header value', kind: 'secret', required: true, hint: 'Sent exactly as typed, in the header named above, on every request.' },
  ],
};

/**
 * What each method SENDS, in plain language, for the connect form and its help panel (task 104).
 * Each line describes `buildAuthHeaders`/`withOAuthAccessToken` (shared/utils/src/auth-headers.ts)
 * as built, so the help cannot promise behaviour the wire does not have.
 */
export const AUTH_METHOD_HELP: Readonly<Record<string, string>> = {
  'api-key': 'A key sent in a request header: X-API-Key unless you name another header.',
  'bearer': 'A token sent as "Authorization: Bearer <token>". Use this for Stripe keys and most personal access tokens.',
  'basic': 'A username and password sent as HTTP Basic authentication.',
  'oauth2': 'A client id and secret that Inferrex exchanges for a short-lived access token at the provider\'s token URL, renewed automatically, and sends as a bearer token unless you name another header. For Salesforce External Client Apps, Xero Custom Connections and Shopify Dev Dashboard apps.',
  'aws-sigv4': 'An access key pair that signs every request, for S3 and S3-compatible storage (R2, MinIO, Wasabi, B2).',
  'jwt': 'A pre-issued JSON Web Token sent as a bearer token. Inferrex does not renew it.',
  'mtls': 'A client certificate presented when the connection opens.',
  'custom': 'Any single header and value the provider asks for.',
  'none': 'No credential. Only for public APIs.',
};

/** How a `list` credential is stored: space-delimited, the OAuth `scope` wire format. */
export const CREDENTIAL_LIST_SEPARATOR = ' ';

/** A stored `list` credential value read back as its entries. */
export function credentialListEntries(stored: string | undefined | null): string[] {
  return (stored ?? '').split(/\s+/).map((s) => s.trim()).filter((s) => s.length > 0);
}

/** Entries joined into the stored form of a `list` credential (de-duplicated, order kept). */
export function credentialListValue(entries: readonly string[]): string {
  return [...new Set(entries.map((e) => e.trim()).filter((e) => e.length > 0))].join(CREDENTIAL_LIST_SEPARATOR);
}

/** Fields for a method, or `[]` for an unrecognised one (open-vocabulary safe). */
export function credentialFieldsFor(method: string): readonly CredentialField[] {
  return CREDENTIAL_FIELD_SPEC[method as AuthMethod] ?? [];
}

/**
 * The methods the connect form OFFERS. A strict subset of the vocabulary: `mtls` is a real
 * method the inference layer may DETECT on a system, but client certificates are transport-level
 * and the outbound fetch path does not yet supply a custom TLS dispatcher — so offering it would
 * recreate exactly the dead-option bug this file exists to prevent. Add it here the moment the
 * transport is wired.
 */
export const CONNECTABLE_AUTH_METHODS: readonly AuthMethod[] = [
  'api-key', 'bearer', 'basic', 'oauth2', 'aws-sigv4', 'jwt', 'custom', 'none',
];

/** Human label for the auth picker. Falls back to the raw value for an unknown method. */
export const AUTH_METHOD_LABELS: Readonly<Record<string, string>> = {
  'api-key': 'API Key',
  'bearer': 'Bearer Token',
  'basic': 'Basic Auth',
  'oauth2': 'OAuth 2.0',
  'aws-sigv4': 'AWS Signature v4 (S3, R2, MinIO…)',
  'jwt': 'JWT',
  'mtls': 'Mutual TLS',
  'custom': 'Custom Header',
  'none': 'No Auth',
};

// ─── Provider presets (2026-10-08) ──────────────────────────────────────────────────────────

/**
 * What the connect form can fill in once it knows WHO the endpoint belongs to. A Salesforce
 * org's token endpoint is its own domain plus a fixed path, so asking the user to type it (or
 * paste a JSON credential) was effort spent on something we already know. A preset never
 * guesses a secret: it only supplies values that follow from the endpoint, and hides them.
 */
export interface CredentialPreset {
  /** Provider key, matching detect-auth's `provider`. */
  readonly provider: string;
  /** Display name for "Recognised …" copy. */
  readonly label: string;
  readonly authType: AuthMethod;
  /** Field values derived from the endpoint; the form hides these fields. */
  readonly values: Readonly<Record<string, string>>;
  /** Optional fields the provider does not use, also hidden. */
  readonly omit: readonly string[];
  /** Field labels in the provider's own words, so they match its console. */
  readonly labels: Readonly<Record<string, string>>;
  /** One line telling the user what was filled in and where to find the rest. */
  readonly note: string;
}

interface PresetRule {
  readonly matches: (host: string) => boolean;
  readonly build: (origin: string) => CredentialPreset;
}

const PRESET_RULES: readonly PresetRule[] = [
  {
    // My Domain (`acme.my.salesforce.com`), sandboxes (`acme--dev.sandbox.my.salesforce.com`)
    // and the older instance hosts all take the token request on the org's own domain.
    matches: (host) => host.endsWith('.salesforce.com') || host.endsWith('.force.com'),
    build: (origin) => ({
      provider: 'salesforce',
      label: 'Salesforce',
      authType: 'oauth2',
      values: { tokenUrl: `${origin}/services/oauth2/token` },
      omit: ['scopes'],
      labels: { clientId: 'Consumer Key', clientSecret: 'Consumer Secret' },
      note: 'Token URL set from your Salesforce domain. Copy the Consumer Key and Secret from your External Client App, which needs the Client Credentials Flow enabled.',
    }),
  },
  {
    matches: (host) => host === 'api.xero.com',
    build: () => ({
      provider: 'xero',
      label: 'Xero',
      authType: 'oauth2',
      values: { tokenUrl: 'https://identity.xero.com/connect/token' },
      omit: [],
      labels: { clientId: 'Client id', clientSecret: 'Client secret' },
      note: 'Token URL set for Xero. Copy the client id and secret from your Custom Connection app.',
    }),
  },
  {
    // Since 2026-01-01 new Shopify apps come from the Dev Dashboard and authenticate with the
    // client credentials grant on the store's own domain; the Admin API reads the token only
    // from X-Shopify-Access-Token. Scopes are fixed on the app's released version.
    matches: (host) => host.endsWith('.myshopify.com'),
    build: (origin) => ({
      provider: 'shopify',
      label: 'Shopify',
      authType: 'oauth2',
      values: { tokenUrl: `${origin}/admin/oauth/access_token`, headerName: 'X-Shopify-Access-Token' },
      omit: ['scopes'],
      labels: { clientId: 'Client ID', clientSecret: 'Secret' },
      note: 'Token URL and header set for your store. Copy the Client ID and Secret from your Dev Dashboard app, which must be installed on this store and owned by the same organization.',
    }),
  },
];

/** The preset for an endpoint, or null when it belongs to no provider we can fill in for. */
export function credentialPresetFor(endpoint: string): CredentialPreset | null {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  const host = url.hostname.toLowerCase();
  const rule = PRESET_RULES.find((r) => r.matches(host));
  return rule ? rule.build(url.origin) : null;
}
